/**
 * JHipster-style authority guard components for conditional rendering.
 * Provides JSX components for role-based access control.
 */

import React from 'react';
import { useAuthorities, Authority } from '../../hooks/useAuthorities';

/**
 * Component wrapper for conditional rendering based on authorities
 */
interface HasAuthorityProps {
  authority?: Authority;
  authorities?: Authority[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const HasAuthority: React.FC<HasAuthorityProps> = ({ 
  authority, 
  authorities, 
  children, 
  fallback = null 
}) => {
  const { hasAuthority, hasAnyAuthority } = useAuthorities();
  
  let hasPermission = false;
  
  if (authority) {
    hasPermission = hasAuthority(authority);
  } else if (authorities) {
    hasPermission = hasAnyAuthority(authorities);
  }
  
  return hasPermission ? <>{children}</> : <>{fallback}</>;
};

/**
 * Higher-order component for protecting routes based on authorities
 */
export const withAuthority = <P extends object>(
  Component: React.ComponentType<P>,
  requiredAuthorities: Authority[]
) => {
  const WrappedComponent = (props: P) => {
    const { hasAnyAuthority } = useAuthorities();
    
    if (!hasAnyAuthority(requiredAuthorities)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
  
  WrappedComponent.displayName = `withAuthority(${Component.displayName || Component.name})`;
  return WrappedComponent;
};
